import React, { useEffect, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import { apiService } from '../services/api';
import { CountryStats, Operator } from '../types';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const WorldMap: React.FC = () => {
  const [countryData, setCountryData] = useState<CountryStats[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryStats | null>(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(1); // days
  const [countryOperators, setCountryOperators] = useState<Operator[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    fetchCountryData();
  }, [period]);

  const fetchCountryData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStatsByCountry({ days: period });
      console.log('Country data loaded:', data);
      setCountryData(data);
    } catch (error) {
      console.error('Error fetching country data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create a map of ISO2 codes to registration counts
  const countryMap = new Map(
    countryData.map(country => [country.country_iso2, country])
  );

  // Calculate color scale
  const maxCount = Math.max(...countryData.map(c => c.registration_count), 1);
  console.log('Max count:', maxCount, 'Countries with data:', countryData.length);
  const colorScale = scaleLinear<string>()
    .domain([0, maxCount])
    .range(['#EFF6FF', '#1E40AF']);

  const handleCountryClick = async (geo: any) => {
    console.log('Country clicked:', geo.properties.NAME);
    const iso2 = geo.properties.ISO_A2;
    const data = countryMap.get(iso2);
    console.log('Country data:', data);
    if (data) {
      setSelectedCountry(data);
      setLoadingOperators(true);
      try {
        // Fetch operators for this country
        console.log('Fetching operators for:', data.country);
        const response = await apiService.getOperators({
          country: data.country,
          size: 100, // Get all operators for the country
        });
        console.log('Operators response:', response);
        setCountryOperators(response.items || []);
      } catch (error) {
        console.error('Error fetching country operators:', error);
        setCountryOperators([]);
      } finally {
        setLoadingOperators(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">World Map</h1>
        <p className="mt-2 text-sm text-gray-700">
          Interactive world map showing registrations by country. Click on a country for details.
        </p>
      </div>

      {/* Debug Info */}
      <div className="bg-yellow-50 p-4 rounded text-sm space-y-2">
        <div>Debug: {countryData.length} countries with data. Max registrations: {maxCount}</div>
        <div>Top 5 countries with data:</div>
        <div className="grid grid-cols-5 gap-2">
          {countryData.slice(0, 5).map((country, idx) => (
            <div key={idx} className="bg-blue-100 p-1 rounded">
              {country.country} ({country.country_iso2}): {country.registration_count}
            </div>
          ))}
        </div>
        <div className="mt-2">
          <button
            onClick={() => {
              // Test - when clicked, show Spain on map
              const spainData = countryMap.get('ES');
              console.log('Spain data from map:', spainData);
              console.log('All country map entries:', Array.from(countryMap.entries()));
            }}
            className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
          >
            Test: Log country data
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Period:</span>
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white shadow rounded-lg p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading map data...</p>
            </div>
          </div>
        ) : (
          <div className="relative">
            <ComposableMap
              projection="geoNaturalEarth1"
              projectionConfig={{
                scale: 160,
              }}
              style={{
                width: '100%',
                height: '500px',
              }}
            >
              <ZoomableGroup>
                <Geographies geography={geoUrl}>
                  {({ geographies }: { geographies: any[] }) =>
                    geographies.map((geo: any) => {
                      // Try different ISO properties
                      let iso2 = geo.properties.ISO_A2;
                      
                      // Skip invalid ISO codes
                      if (iso2 === '-99' || iso2 === -99 || !iso2) {
                        iso2 = null;
                      }
                      
                      const data = iso2 ? countryMap.get(iso2) : null;
                      const registrations = data?.registration_count || 0;
                      
                      // Debug specific countries
                      if (geo.properties.NAME === 'Spain' || geo.properties.NAME === 'Luxembourg' || geo.properties.NAME === 'Colombia') {
                        console.log('Debug country:', geo.properties.NAME, 'ISO_A2:', geo.properties.ISO_A2, 'Found data:', !!data);
                      }

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onClick={() => handleCountryClick(geo)}
                          onMouseEnter={() => {
                            const countryName = geo.properties.NAME;
                            setTooltipContent(
                              data
                                ? `${countryName}: ${registrations} registrations`
                                : `${countryName}: No data`
                            );
                          }}
                          onMouseLeave={() => {
                            setTooltipContent('');
                          }}
                          style={{
                            default: {
                              fill: data ? '#1E40AF' : '#E5E7EB',
                              stroke: '#FFFFFF',
                              strokeWidth: 0.5,
                              outline: 'none',
                            },
                            hover: {
                              fill: data ? '#2563EB' : '#D1D5DB',
                              stroke: '#FFFFFF',
                              strokeWidth: 0.5,
                              outline: 'none',
                            },
                            pressed: {
                              fill: '#1E40AF',
                              stroke: '#FFFFFF',
                              strokeWidth: 0.5,
                              outline: 'none',
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            {/* Tooltip */}
            {tooltipContent && (
              <div className="absolute bottom-4 left-4 bg-gray-800 text-white px-3 py-2 rounded-md text-sm">
                {tooltipContent}
              </div>
            )}

            {/* Legend */}
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">Registrations</h3>
              <div className="space-y-1">
                <div className="flex items-center">
                  <div className="w-6 h-3 bg-blue-900 mr-2"></div>
                  <span className="text-xs text-gray-600">{maxCount}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-3 bg-blue-600 mr-2"></div>
                  <span className="text-xs text-gray-600">{Math.floor(maxCount / 2)}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-3 bg-blue-100 mr-2"></div>
                  <span className="text-xs text-gray-600">0</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Country Details Panel */}
      {selectedCountry && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedCountry.country}
            </h2>
            <button
              onClick={() => setSelectedCountry(null)}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Total Registrations</p>
              <p className="text-2xl font-bold text-gray-900">
                {selectedCountry.registration_count.toLocaleString()}
              </p>
            </div>

            {/* Operators List */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Operators in {selectedCountry.country}</h3>
              {loadingOperators ? (
                <p className="text-sm text-gray-500">Loading operators...</p>
              ) : countryOperators.length === 0 ? (
                <p className="text-sm text-gray-500">No operators found</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {countryOperators.map((operator) => (
                    <div
                      key={operator.id}
                      className={`flex items-center justify-between p-2 rounded-md ${
                        operator.is_allowed ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            operator.is_allowed ? 'bg-green-500' : 'bg-red-500'
                          }`}></span>
                          <span className="text-sm font-medium text-gray-900">
                            {operator.tadig_code}
                          </span>
                          <span className="ml-2 text-sm text-gray-500">
                            {operator.operator_name}
                          </span>
                        </div>
                        <div className="ml-4 text-xs text-gray-500">
                          MCC+MNC: {operator.mcc_mnc}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          operator.is_allowed
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        }`}>
                          {operator.is_allowed ? 'Allowed' : 'Denied'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  Total: {countryOperators.length} operators
                </span>
                <span className="text-gray-500">
                  Allowed: {countryOperators.filter(op => op.is_allowed).length} |
                  Denied: {countryOperators.filter(op => !op.is_allowed).length}
                </span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => {
                  window.location.href = `/operators?country=${encodeURIComponent(selectedCountry.country)}`;
                }}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                View all operators in {selectedCountry.country} →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMap;